import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ClothedEyeError from './ClothedEyeError';
import './ErrorPreview.css';

/**
 * Error Preview Page
 *
 * Displays all error states with tabs to switch between them
 * FOR DEVELOPMENT/REVIEW ONLY - NOT FOR PRODUCTION
 */
const ErrorPreview = () => {
  const [activeTab, setActiveTab] = useState('generic');

  const errorTypes = [
    { id: 'generic', label: 'Generic Error', description: 'Default error state' },
    { id: '404', label: '404 Not Found', description: 'Page not found error' },
    { id: '500', label: '500 Server Error', description: 'Server error' },
    { id: '403', label: '403 Forbidden', description: 'Access forbidden' },
    { id: 'network', label: 'Network Error', description: 'Connection error' },
    { id: 'boundary', label: 'React Error Boundary', description: 'Component crash' },
  ];

  const currentError = errorTypes.find((type) => type.id === activeTab);

  return (
    <div className="error-preview">
      {/* Header with title and info */}
      <div className="error-preview__header">
        <h1 className="error-preview__title">🎨 Error State Preview</h1>
        <p className="error-preview__subtitle">
          Review all error states - select a tab to preview each error type
        </p>
      </div>

      {/* Tab navigation */}
      <div className="error-preview__tabs">
        {errorTypes.map((type) => (
          <button
            key={type.id}
            onClick={() => setActiveTab(type.id)}
            className={`error-preview__tab ${activeTab === type.id ? 'error-preview__tab--active' : ''}`}
          >
            <span className="error-preview__tab-label">{type.label}</span>
            <span className="error-preview__tab-description">{type.description}</span>
          </button>
        ))}
      </div>

      {/* Current error display info */}
      <div className="error-preview__info">
        <div className="error-preview__info-badge">
          Currently viewing: <strong>{currentError.label}</strong>
        </div>
        <div className="error-preview__info-description">{currentError.description}</div>
      </div>

      {/* Error component display */}
      <div className="error-preview__display">
        <ClothedEyeError
          errorType={activeTab}
          size="medium"
          theme="dark"
          fullscreen={false}
          onRetry={() => {
            console.log('Retry clicked for error type:', activeTab);
            alert(`Retry clicked! (Error type: ${activeTab})`);
          }}
          onGoHome={() => {
            console.log('Go Home clicked for error type:', activeTab);
            alert(`Go Home clicked! (Error type: ${activeTab})`);
          }}
        />
      </div>

      {/* Size variants */}
      <div className="error-preview__variants">
        <h2 className="error-preview__variants-title">Size Variants</h2>

        <div className="error-preview__variant-row">
          <div className="error-preview__variant-label">Small</div>
          <div className="error-preview__variant-display">
            <ClothedEyeError
              errorType={activeTab}
              size="small"
              theme="dark"
              fullscreen={false}
              onRetry={() => console.log('Small retry')}
              onGoHome={() => console.log('Small go home')}
            />
          </div>
        </div>

        <div className="error-preview__variant-row">
          <div className="error-preview__variant-label">Medium (Default)</div>
          <div className="error-preview__variant-display">
            <ClothedEyeError
              errorType={activeTab}
              size="medium"
              theme="dark"
              fullscreen={false}
              onRetry={() => console.log('Medium retry')}
              onGoHome={() => console.log('Medium go home')}
            />
          </div>
        </div>

        <div className="error-preview__variant-row">
          <div className="error-preview__variant-label">Large</div>
          <div className="error-preview__variant-display">
            <ClothedEyeError
              errorType={activeTab}
              size="large"
              theme="dark"
              fullscreen={false}
              onRetry={() => console.log('Large retry')}
              onGoHome={() => console.log('Large go home')}
            />
          </div>
        </div>
      </div>

      {/* Theme variants */}
      <div className="error-preview__variants">
        <h2 className="error-preview__variants-title">Theme Variants</h2>

        <div className="error-preview__variant-row">
          <div className="error-preview__variant-label">Dark Theme</div>
          <div className="error-preview__variant-display error-preview__variant-display--dark">
            <ClothedEyeError
              errorType={activeTab}
              size="medium"
              theme="dark"
              fullscreen={false}
              onRetry={() => console.log('Dark retry')}
              onGoHome={() => console.log('Dark go home')}
            />
          </div>
        </div>

        <div className="error-preview__variant-row">
          <div className="error-preview__variant-label">Light Theme</div>
          <div className="error-preview__variant-display error-preview__variant-display--light">
            <ClothedEyeError
              errorType={activeTab}
              size="medium"
              theme="light"
              fullscreen={false}
              onRetry={() => console.log('Light retry')}
              onGoHome={() => console.log('Light go home')}
            />
          </div>
        </div>
      </div>

      {/* Developer notes */}
      <div className="error-preview__notes">
        <h3>📝 Developer Notes</h3>
        <ul>
          <li>
            <strong>Generic Error:</strong> Used for unexpected errors and fallback error states
          </li>
          <li>
            <strong>404 Not Found:</strong> When a route/page doesn't exist
          </li>
          <li>
            <strong>500 Server Error:</strong> Backend/API server errors
          </li>
          <li>
            <strong>403 Forbidden:</strong> Permission/authentication errors
          </li>
          <li>
            <strong>Network Error:</strong> Connection issues, offline state
          </li>
          <li>
            <strong>React Error Boundary:</strong> Component crash/rendering errors
          </li>
        </ul>
        <p className="error-preview__warning">
          ⚠️ This preview page is for development only and should NOT be deployed to production.
        </p>
      </div>
    </div>
  );
};

// ErrorPreview has no props - it's a standalone page component
ErrorPreview.propTypes = {};

export default ErrorPreview;
